import React from 'react';
import { sanityClient } from '../sanityClient';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { Helmet } from 'react-helmet-async';
import { AboutFaqSection } from '@/components/about/AboutFaqSection';
import { AboutFooter } from '@/components/about/AboutFooter';
import { OtherPagesHeader } from '@/components/about/OtherPagesHeader';
import { useQuery } from '@tanstack/react-query';
import MobileBottomNav from '@/components/dashboard/MobileBottomNav';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, Search, ChevronLeft, ChevronRight, User } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

// Safe way to check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

export interface Post {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt?: string;
  mainImage?: { asset: { url: string } };
  author?: {
    name: string;
    image?: {
      asset: {
        url: string;
      }
    }
  };
  categories?: { title: string }[];
  publishedAt?: string;
}

// For server-side rendering
export async function fetchBlogPosts(): Promise<Post[]> {
  try {
    // Fetch from Sanity
    const sanityPosts = await sanityClient.fetch(`
      *[_type == "post"] | order(publishedAt desc) {
        _id,
        title,
        slug,
        excerpt,
        mainImage {
          asset-> {
            url
          }
        },
        author-> {
          name,
          image {
            asset-> {
              url
            }
          }
        },
        categories[]-> {
          title
        },
        publishedAt
      }
    `);
    
    // Fetch user blogs from Supabase (client-side only)
    // Note: This will be handled by React Query on the client side
    // We return Sanity posts here for SSR compatibility
    
    return sanityPosts || [];
  } catch (error) {
    console.error("Error fetching blog posts:", error);
    throw error;
  }
}

const Blog: React.FC = () => {
  const [search, setSearch] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Pagination settings
  const POSTS_PER_PAGE = 12;

  // Use React Query to manage blog posts data
  const { 
    data: sanityPosts = [], 
    isLoading: isLoadingSanity, 
    isError: isErrorSanity, 
    error: errorSanity,
    refetch: refetchSanity 
  } = useQuery({
    queryKey: ['blogPosts'],
    queryFn: fetchBlogPosts,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    retry: 2, // Retry failed requests twice
    refetchOnMount: true,
    enabled: true
  });

  // Fetch user blogs from Supabase
  const { 
    data: userBlogs = [], 
    isLoading: isLoadingUserBlogs,
    error: userBlogsError
  } = useQuery({
    queryKey: ['userBlogPosts'],
    queryFn: async () => {
      // Fetch blogs first
      const { data: blogs, error: blogsError } = await supabase
        .from('user_blogs')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false });
      
      if (blogsError) throw blogsError;
      if (!blogs || blogs.length === 0) return [];
      
      // Fetch profiles separately for each unique user_id
      const userIds = [...new Set(blogs.map(b => b.user_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, photo_url')
        .in('id', userIds);
      
      // Create a map of user_id to profile
      const profileMap = new Map();
      if (profiles) {
        profiles.forEach(profile => {
          profileMap.set(profile.id, profile);
        });
      }
      
      // Helper function to extract text from HTML (works in browser and SSR)
      const extractTextFromHTML = (html: string, maxLength: number = 160): string => {
        if (!html) return '';
        // Strip HTML tags and decode entities
        let text = html
          .replace(/<[^>]*>/g, '') // Remove HTML tags
          .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
          .replace(/&amp;/g, '&') // Decode &amp;
          .replace(/&lt;/g, '<') // Decode &lt;
          .replace(/&gt;/g, '>') // Decode &gt;
          .replace(/&quot;/g, '"') // Decode &quot;
          .replace(/&#39;/g, "'") // Decode &#39;
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim();
        return text.substring(0, maxLength);
      };

      // Transform to match Post interface
      return blogs.map(blog => {
        const profile = profileMap.get(blog.user_id);
        // Use excerpt if available, otherwise extract from content_html
        const excerpt = blog.excerpt || (blog.content_html ? extractTextFromHTML(blog.content_html, 160) : '');
        return {
          _id: blog.id,
          title: blog.title,
          slug: { current: blog.slug },
          excerpt: excerpt,
          mainImage: blog.featured_image_url ? {
            asset: { url: blog.featured_image_url }
          } : undefined,
          author: {
            name: profile 
              ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Metefy User'
              : 'Metefy User',
            image: profile?.photo_url ? {
              asset: { url: profile.photo_url }
            } : undefined
          },
          categories: undefined,
          publishedAt: blog.published_at,
          _source: 'supabase'
        };
      });
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });
  
  if (userBlogsError) {
    console.error('Error fetching user blogs:', userBlogsError);
  }

  // Combine both sources
  const posts = React.useMemo(() => {
    const allPosts = [...sanityPosts, ...userBlogs];
    // Sort by published date (most recent first)
    return allPosts.sort((a, b) => {
      const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [sanityPosts, userBlogs]);

  const isLoading = isLoadingSanity || isLoadingUserBlogs;
  const isError = isErrorSanity;
  const error = errorSanity;
  const refetch = () => {
    refetchSanity();
  };

  const filteredPosts = React.useMemo(() => {
    if (!posts?.length) return [];
    const searchLower = search.toLowerCase();
    return posts.filter(post => (
      post.title.toLowerCase().includes(searchLower) ||
      (post.excerpt && post.excerpt.toLowerCase().includes(searchLower)) ||
      (post.categories && post.categories.some(cat => cat.title.toLowerCase().includes(searchLower)))
    ));
  }, [posts, search]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Handle retry
  const handleRetry = React.useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <>
      <Helmet>
        <title>Metefy Blog – Tips, Updates & Stories</title>
        <meta name="description" content="Read the latest tips, updates, and stories from Metefy. Stay informed about lost & found solutions and community news." />
        <meta property="og:title" content="Metefy Blog – Tips, Updates & Stories" />
        <meta property="og:description" content="Read the latest tips, updates, and stories from Metefy. Stay informed about lost & found solutions and community news." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.metefy.com/blog" />
        <meta property="og:image" content="https://www.metefy.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Metefy Blog – Tips, Updates & Stories" />
        <meta name="twitter:description" content="Read the latest tips, updates, and stories from Metefy. Stay informed about lost & found solutions and community news." />
        <meta name="twitter:image" content="https://www.metefy.com/og-image.png" />
        <link rel="canonical" href="https://www.metefy.com/blog" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <OtherPagesHeader />
        <main className="bg-background min-h-screen pt-20">
          <div className="container max-w-6xl mx-auto px-4 py-12">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 tracking-tight">
                Metefy <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">Blog</span>
              </h1>
              <p className="text-center text-xl md:text-2xl text-foreground/80 mb-12 max-w-2xl mx-auto leading-relaxed">
                Discover the latest updates, tips, and stories from the Metefy community.
              </p>
            </div>
            {/* Search Bar */}
            <div className="flex justify-center mb-10">
              <div className="relative w-full max-w-lg">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search blogs by title, category, or excerpt..."
                  className="w-full px-6 py-4 border-2 border-border rounded-2xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 text-lg bg-card text-foreground placeholder:text-foreground/60 shadow-lg transition-all duration-300"
                />
                <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-foreground/80 w-5 h-5" />
              </div>
            </div>
            {isError ? (
              <div className="text-center">
                <p className="text-destructive text-lg mb-2">Error loading blog posts</p>
                <p className="text-sm text-muted-foreground mb-4">{error instanceof Error ? error.message : 'Please try again later'}</p>
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : isLoading ? (
              <div className="text-center">
                <p className="text-foreground/80 text-lg">Loading blog posts...</p>
              </div>
            ) : !posts?.length ? (
              <div className="text-center">
                <p className="text-muted-foreground text-lg mb-4">No blog posts available.</p>
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
                >
                  Refresh
                </button>
              </div>
            ) : filteredPosts.length === 0 ? (
              <p className="text-center text-muted-foreground text-lg">
                No matching blog posts found.
              </p>
            ) : (
              <>
                <div className="grid gap-8 md:gap-10 md:grid-cols-2 lg:grid-cols-3">
                  {currentPosts.map(post => {
                    const blogUrl = (post as any)._source === 'supabase' 
                      ? `/blog/user/${post.slug.current}`
                      : `/blog/${post.slug.current}`;
                    return (
                  <div
                    key={post._id}
                    className="bg-card rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden border border-border hover:border-primary group hover:-translate-y-1"
                  >
                    {post.mainImage?.asset?.url && (
                      <Link to={blogUrl}>
                        <div className="relative overflow-hidden">
                          <img
                            src={post.mainImage.asset.url}
                            alt={post.title}
                            className="w-full h-52 object-cover object-center group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      </Link>
                    )}
                    <div className="p-6 md:p-8 flex-1 flex flex-col space-y-4">
                      <h2 className="text-2xl md:text-3xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors duration-200 leading-tight">
                        <Link to={blogUrl}>{post.title}</Link>
                      </h2>
                      {post.excerpt && (
                        <p className="text-foreground/80 text-base md:text-lg mb-4 line-clamp-3 leading-relaxed">{post.excerpt}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground/80">
                        {post.author && (
                          <div className="flex items-center gap-2">
                            {post.author.image?.asset?.url && (
                              <img
                                src={post.author.image.asset.url}
                                alt={post.author.name}
                                className="w-6 h-6 rounded-full object-cover ring-2 ring-primary/20"
                              />
                            )}
                            <span className="font-semibold text-primary">{post.author.name}</span>
                          </div>
                        )}
                        {post.categories && post.categories.length > 0 && (
                          <span>|
                            {post.categories.map((cat, idx) => (
                              <span key={cat.title} className="ml-1">
                                {cat.title}{idx < post.categories.length - 1 ? ',' : ''}
                              </span>
                            ))}
                          </span>
                        )}
                        {post.publishedAt && (
                          <span>|
                            <span className="ml-1">{new Date(post.publishedAt).toLocaleDateString()}</span>
                          </span>
                        )}
                      </div>
                      <Link
                        to={blogUrl}
                        className="mt-auto inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition-colors group/link"
                      >
                        Read More
                        <span className="group-hover/link:translate-x-1 transition-transform duration-200">→</span>
                      </Link>
                    </div>
                  </div>
                  );
                  })}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-12 flex justify-center">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage > 1) {
                                setCurrentPage(currentPage - 1);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }
                            }}
                            className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                        
                        {/* Page numbers */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                          // Show first page, last page, current page, and pages around current
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          ) {
                            return (
                              <PaginationItem key={page}>
                                <PaginationLink
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setCurrentPage(page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  isActive={currentPage === page}
                                  className="cursor-pointer"
                                >
                                  {page}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          } else if (
                            page === currentPage - 2 ||
                            page === currentPage + 2
                          ) {
                            return (
                              <PaginationItem key={page}>
                                <PaginationEllipsis />
                              </PaginationItem>
                            );
                          }
                          return null;
                        })}
                        
                        <PaginationItem>
                          <PaginationNext
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage < totalPages) {
                                setCurrentPage(currentPage + 1);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }
                            }}
                            className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
                
                {/* Results count */}
                <div className="mt-6 text-center text-sm text-foreground/80">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredPosts.length)} of {filteredPosts.length} posts
                </div>
              </>
            )}
          </div>
        </main>
        <AboutFaqSection />
        <AboutFooter />
        {user && <MobileBottomNav />}
      </div>
    </>
  );
};

export default Blog;
